<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarDaysIcon, CreditCardIcon, UserCircleIcon, ChevronUpIcon } from '@heroicons/vue/20/solid';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/vue/20/solid'
import CompanyModal from "../modals/CompanyModal.vue";
import CouponHistoryDetail from "./CouponHistoryDetail.vue";
import TicketStatusBadge from "./TicketStatusBadge.vue";
import InvoluntaryBadge from "./InvoluntaryBadge.vue";

import { UsersTicketsHistorySchema } from '../../interfaces';
import { FwbAvatar } from 'flowbite-vue'
import { PlaneLanding, PlaneTakeoff, TicketIcon, TicketCheckIcon, TicketSlashIcon, TicketXIcon, TicketMinusIcon, CircleAlertIcon, ListTodoIcon, AsteriskIcon, EarthIcon, RadarIcon, LoaderIcon, LocateFixedIcon, ActivityIcon, SquareActivity, TimerIcon } from 'lucide-vue-next';
import { formatInTimeZone } from 'date-fns-tz';
import { useClaimStore } from '@/store';


type UserTicketHistory  = UsersTicketsHistorySchema[number]

const props = defineProps<{ ticket: UserTicketHistory["tickets"][number] }>();
// const ticket = reactive(props.ticket)

function formatFlightDate(date: Date, tz: string, format: string = 'HH:mm') {
  return formatInTimeZone(date, tz, format, { locale: fr })
}

const legsFlightDate = computed(() => {
  return props.ticket.legs.map((legHistory) => {
    return {
      scheduleDepartureLocal: legHistory[0].localDepartureString?.split(" ")[1],
      actualDepartureLocal: legHistory[0].flight.actualDepartureUTC
        ? formatFlightDate(
          new Date(legHistory[0].flight.actualDepartureUTC as Date),
          legHistory[0].flight.departureAirport.timezone
        )
        : null,
      scheduleArrivalLocal: legHistory[0].localArrivalString?.split(" ")[1]
        || formatFlightDate(legHistory[0].flight.scheduledArrivalUTC, legHistory[0].flight.arrivalAirport.timezone, "HH:mm"),
      actualArrivalLocal: legHistory[0].flight.inGateArrivalUTC
        ? formatFlightDate(legHistory[0].flight.inGateArrivalUTC as Date, legHistory[0].flight.arrivalAirport.timezone)
        : null,
    }
  });
});

const currentDate = new Date();
const ticketSyncDate = computed(() => {
  const ticket = props.ticket;
  return format(new Date(ticket.syncedAt || ticket.legs.reverse()[0][0].syncedAt  as Date), 'dd MMMM', { locale: fr })
})
</script>
<template>
<div v-if="ticket?.legs?.length">
  <p class="mb-6 text-sm font-semibold leading-6 text-gray-900">
    <div class="flex items-center justify-between">
      <div class="mb-2">E-Ticket
        <svg viewBox="0 0 2 2" class="mx-1 inline h-0.5 w-0.5 fill-current" aria-hidden="true">
            <circle cx="1" cy="1" r="1" />
          </svg>
        {{ ticket.ticketNumber }}
      </div>
      <div class="text-xs italic font-normal text-gray-500">Synchronisé le {{ ticketSyncDate }}</div>
    </div>

    <Disclosure as="div" class="mb-2 divide-y divide-gray-100" v-for="(legHistory, index) in ticket.legs" :key="index" v-slot="{ open }">
      <DisclosureButton
        :class="[
            'w-full relative block cursor-pointer rounded-lg hover:bg-gray-50  bg-white px-4 py-5 shadow-sm',
            isValid(legHistory[0]?.flight?.scheduledArrivalUTC) && new Date(legHistory[0]?.flight?.scheduledArrivalUTC)?.getTime() > currentDate.getTime() ? 'border border-dashed' : 'border border-gray-300',
            legHistory.some(l => l.flightId === useClaimStore().claim.legalSpecification.impactedSegment.flightId) && 'border-2 my-5 relative border-yellow-500 rounded-lg mx-1 bg-indigo-50'
          ]"
        >
          <div class="text-left">
            <div class="flex justify-between max-w-4xl mx-auto gap-x-4">
              <div class="flex min-w-0 gap-x-4">
                <!-- <img class="flex-none w-12 h-12 rounded-full bg-gray-50" :src="person.imageUrl" alt="" /> -->
                <div v-tooltip="'Compagnie non européenne'">
                  <fwb-avatar
                    :img="legHistory[0].flight?.airline?.legalData?.iconURL"
                    :initials="legHistory[0].flight.airline?.iata"
                    :status="legHistory[0].flight.airline?.isEC261 ? 'online' : 'away'"
                  />
                </div>

                <div class="flex-auto min-w-0">
                  <p class="text-sm font-semibold leading-6 text-gray-900">
                    <span class="absolute inset-x-0 bottom-0 -top-px" />
                    Vol
                      <span class="font-semibold text-gray-700">
                        <a :href="`https://fr.flightaware.com/live/flight/${legHistory[0].flight.flightIdentifier}/history`" target="_blank" class="font-semibold text-gray-700 link">
                          {{ legHistory[0].flight.flightIdentifier }}
                        </a>
                      </span>
                      opéré par {{ legHistory[0].flight.airline?.name }}
                  </p>
                  <p class="flex-row gap-10 mt-1 text-sm leading-5 text-gray-600">
                    <div class="relative inline-flex items-center">
                        <p class="inline-flex">
                          <PlaneTakeoff :size="14" class="mr-1" />
                          Départ : {{ legsFlightDate[index].scheduleDepartureLocal }}
                      </p>
                      <p class="inline-flex text-sm font-semibold leading-7">
                        <span class="mx-1.5 font-bold text-gray-800" v-if="legHistory[0].flight.monitoringStatus === 'CANCELLED' || legHistory[0].flight.monitoringStatus === 'DELAYED'">→</span>
                        <span v-if="legHistory[0].flight.monitoringStatus === 'CANCELLED'" class="text-gray-900"><pre>vol annulé</pre></span>
                        <span v-else-if="legHistory[0].flight.monitoringStatus === 'DELAYED'">
                          <!-- Retardé -->
                          <span v-if="legsFlightDate[index].actualDepartureLocal" class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                            <!-- <ActivityIcon class="w-4 h-4 " color="brown" /> -->
                            <TimerIcon class="w-4 h-4 " color="orange" />
                            <!-- <svg class="h-1.5 w-1.5 fill-yellow-500" viewBox="0 0 6 6" aria-hidden="true">
                              <circle cx="3" cy="3" r="3" />
                            </svg> -->
                            Réel : {{ legsFlightDate[index].actualDepartureLocal }}
                          </span>
                        </span>
                      </p>
                    </div>
                    <div class="relative mt-0.5 hover:underline">
                      <p class="inline-flex items-center">
                        <PlaneLanding :size="14" class="mr-1" />
                        Arrivée : {{ legsFlightDate[index].scheduleArrivalLocal }}
                        <p class="inline-flex text-sm font-semibold leading-7">
                          <span class="mx-1.5 font-bold text-gray-800" v-if="legHistory[0].flight.monitoringStatus === 'CANCELLED' || legHistory[0].flight.monitoringStatus === 'DELAYED'">→</span>
                          <pre v-if="legHistory[0].flight.monitoringStatus === 'CANCELLED'" class="text-gray-900">vol annulé</pre>
                          <span v-else-if="legHistory[0].flight.monitoringStatus === 'DELAYED'">
                            <!-- Retardé -->
                            <span v-if="legsFlightDate[index].actualArrivalLocal" class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                              <TimerIcon class="w-4 h-4 " color="orange" />
                              <!-- <svg class="h-1.5 w-1.5 fill-yellow-500" viewBox="0 0 6 6" aria-hidden="true">
                                <circle cx="3" cy="3" r="3" />
                              </svg> -->
                              Réel : {{ legsFlightDate[index].actualArrivalLocal }}
                            </span>
                          </span>
                          <!-- <span v-else-if="legHistory[0].flight.monitoringStatus === 'IN_GATE'"> -->
                            <!-- <span v-if="legHistory[0].flight?.actualDepartureUTC" class="italic inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200"> -->
                              <!-- <ActivityIcon class="w-4 h-4 " color="blue" /> -->
                              <!-- <svg class="h-1.5 w-1.5 fill-blue-500" viewBox="0 0 6 6" aria-hidden="true"> -->
                                <!-- <circle cx="3" cy="3" r="3" /> -->
                              <!-- </svg> -->
                              <!-- À l'heure -->
                            <!-- </span> -->
                          <!-- </span> -->
                        </p>
                      </p>
                    </div>
                  </p>
                </div>
              </div>


              <div class="flex items-center shrink-0 gap-x-4">
                <div class="hidden sm:flex sm:flex-col sm:items-end">
                  <p class="text-xs font-normal leading-6 text-gray-600">{{ legHistory[0].localDepartureString?.split(" ")[0] }}</p>
                  <p class="text-sm font-bold leading-6 text-gray-900">{{ legHistory[0].flight.departureAirport.iata }} → {{ legHistory[0].flight.arrivalAirport.iata }}</p>
                  <InvoluntaryBadge :involuntaryIndicator="legHistory[0].involuntaryIndicator" />
                  <p v-if="true" class="mt-1 text-xs leading-5 text-gray-500">
                    <TicketStatusBadge :status="legHistory[0].status" :status-description="legHistory[0].statusDescription" />
                  </p>
                  <!-- <span v-if="legHistory[0].involuntaryIndicator" class="inline-flex mt-1 items-center gap-x-1.5 rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                    <svg class="h-1.5 w-1.5 fill-red-500" viewBox="0 0 6 6" aria-hidden="true">
                      <circle cx="3" cy="3" r="3" />
                    </svg>
                    {{ legHistory[0].involuntaryIndicator }}
                  </span> -->
                  <!-- <div v-if="legHistory[0].involuntaryIndicator" class="inline-flex mt-1 items-center gap-x-1.5 rounded-full px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                    <svg class="h-1.5 w-1.5 fill-red-500" viewBox="0 0 6 6" aria-hidden="true">
                      <circle cx="3" cy="3" r="3" />
                    </svg>
                    {{legHistory[0].involuntaryIndicator}}
                  </div> -->
                  <!-- <div v-if="legHistory[0].involuntaryIndicator" class="mt-1 flex items-center gap-x-1.5">
                    <div class="flex-none p-1 rounded-full bg-red-600/20">
                      <div class="h-1.5 w-1.5 rounded-full bg-red-600" />
                    </div>
                    <p class="text-xs leading-5 text-gray-900">{{legHistory[0].involuntaryIndicator}}</p>
                  </div> -->
                </div>
                <ChevronRightIcon v-if="open == false" class="flex-none w-5 h-5 text-gray-400" aria-hidden="true" />
                <ChevronDownIcon v-else class="flex-none w-5 h-5 text-gray-400" aria-hidden="true" />
              </div>
            </div>
          </div>


        <!-- <span class="text-gray-600">
          <fwb-avatar
            :img="legHistory[0].flight?.airline?.legalData?.iconURL"
            :initials="legHistory[0].flight.airline?.iata"
            :status="legHistory[0].flight.airline?.isEC261 ? 'online' : 'away'"
          />
        </span>
        <span class="font-medium text-gray-800">{{ legHistory[0].flight.flightIdentifier }}</span>
        <p>
          <span v-if="legHistory[0].flight.departureAirport.isEC261">🇪🇺 </span>{{ legHistory[0].flight.departureAirport.iata }}
          <span aria-hidden="true">&rarr;</span> 
          <span v-if="legHistory[0].flight.arrivalAirport.isEC261"> 🇪🇺 </span>{{ legHistory[0].flight.arrivalAirport.iata }}
        </p>
        <div class="flex-row ">
          <span class="text-gray-600">{{ legHistory[0].localDepartureString }}</span>
          <span class="text-gray-600">{{ legHistory[0].localArrivalString }}</span>
        </div>
        <div class="self-end flex-none px-6 pt-4">
          <dt class="sr-only">Status</dt>
          <dd class="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 rounded-md bg-green-50 ring-1 ring-inset ring-green-600/20">
            {{ legHistory[0].statusDescription }}
          </dd>
        </div>
        <ChevronUpIcon
          :class="open ? 'rotate-180 transform' : ''"
          class="w-5 h-5 text-purple-500"
        /> -->
      </DisclosureButton>
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
      <DisclosurePanel class="px-4 pt-4 pb-2 text-sm text-gray-500">
        <div class="pt-2 pb-8">
          <CouponHistoryDetail :legHistory="legHistory" />
        </div>
      </DisclosurePanel>
      </transition>
    </Disclosure>
  </p>
</div>
</template>